export class SetXML {

    private xmlObject: any;
    private jsonObject: Record<string, any>
    private mapTransform: Record<string, any>
    private appCode: string;
    private dataReader: any;

    constructor(
        mapTransform: Record<string, any>,
        appCode: string,
    ) {
        this.appCode = appCode;
        const filteredMapTransform: Record<string, any> = {};
        
        for (const [key, mappingConfig] of Object.entries(mapTransform)) {
            const mappingConfigs = Array.isArray(mappingConfig) ? mappingConfig : [mappingConfig];
            const filteredConfigs = mappingConfigs.filter(config => {
                return !config.appCode || config.appCode.includes(this.appCode);
            });
            if (filteredConfigs.length > 0) {
                filteredMapTransform[key] = Array.isArray(mappingConfig) ? filteredConfigs : filteredConfigs[0];
            }
        }
        
        this.mapTransform = filteredMapTransform;
    }

    get xml(): any {
        return this.xmlObject;
    }

    set xml(value: any) {
        this.xmlObject = value;
    }

    get json(): Record<string, any> {
        return this.jsonObject;
    }
    get dataRPWDataReader(): any {
        return this.dataReader;
    }
    set dataRPWDataReader(value: any) {
        this.dataReader = value;
    }

    set json(value: Record<string, any>) {
        const toLowerCaseKeys = (obj: Record<string, any>): Record<string, any> => {
            return Object.fromEntries(
                Object.entries(obj).map(([key, val]) => [
                    key.toLowerCase(),
                    val && typeof val === 'object' && !Array.isArray(val) ? toLowerCaseKeys(val) : val
                ])
            );
        };
        this.jsonObject = toLowerCaseKeys(value);
    }

    public transform(): any {
        for (const [jsonKey, mappingConfig] of Object.entries(this.mapTransform)) {
            if (this.jsonObject[jsonKey] === undefined || this.jsonObject[jsonKey] === null) {
                continue;
            }

            const mappingConfigs = Array.isArray(mappingConfig) ? mappingConfig : [mappingConfig];

            for (const config of mappingConfigs) {
                const { xmlNode, nodeMap, type, innerNode } = config; 
                const { nodeFields, recordFields } = nodeMap;

                let xmlNodes: any[];
                
                // Check if this is the BC APSNames structure
                if (type !== undefined && innerNode) {
                    xmlNodes = this.getXMLNodesByPathWithIndex(xmlNode, type, innerNode);
                } else {
                    xmlNodes = this.getXMLNodesByPath(xmlNode);
                }
                
                const jsonData = this.jsonObject[jsonKey];

                this.processMapping(jsonData, xmlNodes, nodeFields, recordFields, xmlNode, innerNode, type);
            }
        }

        return this.xmlObject;
    }

    private getXMLNodesByPath(xmlNodePath: string): any[] {
        const pathSegments = xmlNodePath.replace(/\/$/, '').split('/').filter(segment => segment.length > 0);
        let currentNodes = [this.xmlObject];

        for (const segment of pathSegments) {
            const nextNodes: any[] = [];
            for (const node of currentNodes) {
                if (node.selectNodes) {
                    const foundNodes = node.selectNodes(segment);
                    if (foundNodes && foundNodes.length > 0) {
                        for (let i = 0; i < foundNodes.length; i++) {
                            nextNodes.push(foundNodes[i]);
                        }
                    }
                } else if (node.getElementsByTagName) {
                    const foundNodes = node.getElementsByTagName(segment);
                    if (foundNodes && foundNodes.length > 0) {
                        for (let i = 0; i < foundNodes.length; i++) {
                            nextNodes.push(foundNodes[i]);
                        }
                    }
                } else if (node[segment]) {
                    if (Array.isArray(node[segment])) {
                        nextNodes.push(...node[segment]);
                    } else {
                        nextNodes.push(node[segment]);
                    }
                }
            }
            currentNodes = nextNodes;
        }

        return currentNodes;
    }

    private getXMLNodesByPathWithIndex(xmlNodePath: string, type: string, innerNode: string): any[] {
        // First get the APSNames nodes
        const apsNodes = this.getXMLNodesByPath(xmlNodePath);
        
        if (apsNodes.length === 0) {
            return [];
        }

        let targetNode = null
        for (const node of apsNodes) {
            if (node.getAttribute('Type') === type) {
                targetNode = node;
                break;
            }
        }

        if (!targetNode) {
            return [];
        }

        // Now get the inner nodes (Name nodes)
        const innerNodes: any[] = [];
        if (targetNode.selectNodes) {
            const foundNodes = targetNode.selectNodes(innerNode);
            if (foundNodes && foundNodes.length > 0) {
                for (let i = 0; i < (foundNodes.length); i++) {
                    innerNodes.push(foundNodes[i]);
                }
            }
        } else if (targetNode.getElementsByTagName) {
            const foundNodes = targetNode.getElementsByTagName(innerNode);
            if (foundNodes && foundNodes.length > 0) {
                for (let i = 0; i < foundNodes.length; i++) {
                    innerNodes.push(foundNodes[i]);
                }
            }
        }

        return innerNodes;
    }

    private processMapping(jsonData: any, xmlNodes: any[], nodeFields: string[], recordFields: string[], xmlNodeName: string, innerNode: string, type: string): void {
        const APSNamesFields = ['OnAgreementFirstName', 'OnAgreementMiddleName', 'OnAgreementLastName'];
        if (nodeFields[0] && APSNamesFields.includes(nodeFields[0]) &&
            xmlNodeName === 'APSNames/') {
            this.processAPSNames(jsonData, xmlNodes, nodeFields, recordFields, xmlNodeName, innerNode, type);
        } else if (xmlNodeName === 'APSNames/' && nodeFields[0] === "OnAgreementName"){
            this.processAPSNamesNS(jsonData, xmlNodes, type);
        } else {
            for (const key in jsonData) {
                if (jsonData.hasOwnProperty(key)) {
                    let xmlNodeIndex = 0;

                    const match = key.match(/(\d+)$/);
                    if (match) {
                        xmlNodeIndex = parseInt(match[1], 10) - 1;
                    }

                    if (xmlNodeIndex >= 0) {
                        while (xmlNodeIndex >= xmlNodes.length) {
                            const newXmlNode = this.createNewXmlNode(xmlNodeName);
                            xmlNodes.push(newXmlNode);
                        }
                        const xmlNode = xmlNodes[xmlNodeIndex];
                        for (let j = 0; j < recordFields.length && j < nodeFields.length; j++) {
                            const recordField = recordFields[j];
                            const nodeField = nodeFields[j];

                            const value = this.getValueFromPath(jsonData, recordField, xmlNodeIndex + 1);

                            try{
                                if (value !== undefined) {
                                    this.setXMLValue(xmlNode, nodeField, value);
                                }
                            } catch (error) {
                                console.log(`Unable to set attribute for ${nodeField}: ${error}`);
                            }
                        }
                    }
                }
            }
        }
    }

    private processAPSNames(jsonData: any, xmlNodes: any[], nodeFields: string[], recordFields: string[], xmlNodeName: string, innerNode: string, type: string): void {
        // Transform JSON data into array of name objects with their original indices
        const nameItemsWithIndices = this.transformJsonDataToNameItemsWithIndices(jsonData);
        
        for (const { nameItem, originalIndex } of nameItemsWithIndices) {
            // Check if we need to create a new XML node
            if (originalIndex >= xmlNodes.length) {
                const fullName = [nameItem.firstname, nameItem.middleName, nameItem.lastName]
                    .filter(name => name && name.trim() !== '')
                    .join(' ');
                const newAPSNode = this.createNewAPSNode(fullName, type);
                xmlNodes.push(newAPSNode);
            } else {
                const xmlNode = xmlNodes[originalIndex];
                const fullName = [nameItem.firstname, nameItem.middleName, nameItem.lastName];
                
                for (let j = 0; j < nodeFields.length; j++) {
                    const nodeField = nodeFields[j];
                    if (nodeField.toLowerCase().includes('firstname')) {
                        xmlNode.setAttribute(nodeField, fullName[0]);
                    } else if (nodeField.toLowerCase().includes('middlename')) {
                        xmlNode.setAttribute(nodeField, fullName[1]);
                    } else if (nodeField.toLowerCase().includes('lastname')) {
                        xmlNode.setAttribute(nodeField, fullName[2]);
                    }
                }
                xmlNode.setAttribute('Gender', '');
            }
        }
    }

    private processAPSNamesNS(jsonData: any, xmlNodes: any[], type: string): void {
        // same function except ns has the name as 1 field instead of split
        let originalIndex = 0;
        for (const key in jsonData){
            if (originalIndex++ >= xmlNodes.length) {
                const newAPSNode = this.createNewAPSNode(jsonData[key], type);
                xmlNodes.push(newAPSNode);
            } else {
                const xmlNode = xmlNodes[originalIndex - 1];
                xmlNode?.setAttribute("OnAgreementName", jsonData[key])
                xmlNode?.setAttribute('Gender', '');
                xmlNode?.setAttribute("OnAgreement", "1");
                xmlNode?.setAttribute("OnTitle", "1");
                xmlNode?.setAttribute("OnTitleNameFull", jsonData[key]);
            }
        }
    }

    private transformJsonDataToNameItemsWithIndices(jsonData: any): Array<{nameItem: {firstname: string, middleName: string, lastName: string}, originalIndex: number}> {
        const nameItemsWithIndices: Array<{nameItem: {firstname: string, middleName: string, lastName: string}, originalIndex: number}> = [];
        const processedIndices = new Set<number>();

        // Process each key in the JSON data
        for (const key in jsonData) {
            if (jsonData.hasOwnProperty(key)) {
                // Extract the index from the end of the key
                const match = key.match(/(\d+)$/);
                if (match) {
                    const keyIndex = parseInt(match[1], 10);
                    const arrayIndex = keyIndex - 1; // Convert to 0-based array index
                    
                    // Skip if we've already processed this index
                    if (processedIndices.has(arrayIndex)) {
                        continue;
                    }
                    
                    // Find the three name components for this index
                    const firstName = this.findNameValue(jsonData, 'first', keyIndex) || '';
                    const middleName = this.findNameValue(jsonData, 'middle', keyIndex) || '';
                    const lastName = this.findNameValue(jsonData, 'last', keyIndex) || '';
                    
                    // Store as object with name components and original index
                    nameItemsWithIndices.push({
                        nameItem: {
                            firstname: firstName,
                            middleName: middleName,
                            lastName: lastName
                        },
                        originalIndex: arrayIndex
                    });
                    
                    processedIndices.add(arrayIndex);
                }
            }
        }

        return nameItemsWithIndices;
    }

    private findNameValue(jsonData: any, nameType: string, index: number): string {
        for (const key in jsonData) {
            if (jsonData.hasOwnProperty(key)) {
                // Check if key contains the name type and ends with the correct index
                if (key.toLowerCase().includes(nameType) && key.endsWith(index.toString())) {
                    return jsonData[key] || '';
                }
            }
        }
        return '';
    }

    private createNewXmlNode(xmlNodeName: string): any {
        const xmlPath = xmlNodeName.split('/').filter(segment => segment.length > 0);
        if (xmlPath.length === 0) {
            return {};
        }
        
        let currentParent = this.xmlObject;
        let ownerDoc: any = null;
        if (this.xmlObject && this.xmlObject.ownerDocument) {
            ownerDoc = this.xmlObject.ownerDocument;
        } else if (this.xmlObject && this.xmlObject.parentNode && this.xmlObject.parentNode.ownerDocument) {
            ownerDoc = this.xmlObject.parentNode.ownerDocument;
            currentParent = this.xmlObject.parentNode;
        }
        
        if (!ownerDoc) {
            return {};
        }
        
        let currentNode: any = null;
        
        for (let i = 0; i < xmlPath.length; i++) {
            const segment = xmlPath[i];
            if (segment) {
                const newNode = ownerDoc.createElement(segment);
                if (i === 0) {
                    currentParent.appendChild(newNode);
                } else {
                    currentNode.appendChild(newNode);
                }
                currentNode = newNode;
            }
        }
        
        return currentNode;
    }

    private createNewAPSNode(fullName: string, type: string): any {
        // Use the data reader to create the appropriate node
        if (type === 'Purchasers') {
            // Buyers/Purchasers
            return this.dataReader.addNewPurchaser(fullName, "Person");
        } else if (type === 'Vendors') {
            // Sellers/Vendors
            return this.dataReader.addNewVendor(fullName, "Person");
        }

        return null;
    }

    private setXMLValue(xmlNode: any, nodeField: string, value: any): void {

        if (!xmlNode) {
            xmlNode = this.xml.parentNode.ownerDocument.createElement(nodeField);
            xmlNode.setAttribute(nodeField, value);
            this.xml.appendChild(xmlNode);
            return;
        }

        if (nodeField.startsWith('@')) {
            const attrName = nodeField.substring(1);
            if (xmlNode.setAttribute) {
                xmlNode.setAttribute(attrName, value);
            } else {
                xmlNode[attrName] = value;
            }
        } else {
            xmlNode.setAttribute(nodeField, value);
        }
    }

    private getValueFromPath(jsonData: any, path: string, index: number): any {
        if (!path) return undefined;

        const cleanPath = path.replace(/^\/|\/$/g, '');
        const resolvedPath = cleanPath.replace(/\*/g, index.toString());
        const pathParts = resolvedPath.split('/').filter(part => part.length > 0);
        
        let current = jsonData;
        for (const part of pathParts) {
            if (current && typeof current === 'object') {
                current = current[part];
            } else {
                return undefined;
            }
        }
        
        return current;
    }
}